import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import '../../config/pdfWorker';
import DocViewer, { DocViewerRenderers } from '@cyntler/react-doc-viewer';
import { 
  ZoomInOutlined, 
  ZoomOutOutlined, 
  FullscreenOutlined, 
  FullscreenExitOutlined,
  NavigateBeforeOutlined,
  NavigateNextOutlined,
  RotateLeftOutlined,
  RefreshOutlined,
  ErrorOutlineOutlined
} from '@mui/icons-material';
import '@cyntler/react-doc-viewer/dist/index.css';
import './DocumentViewer.css';

interface DocumentViewerProps {
  file: File | string | null;
  fileType?: 'pdf' | 'docx' | 'auto';
  className?: string;
  height?: number;
  showToolbar?: boolean;
  onError?: (error: string) => void;
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({
  file,
  fileType = 'auto',
  className = '',
  height = 600,
  showToolbar = true,
  onError,
}) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.0);
  const [rotation, setRotation] = useState<number>(0);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [detectedType, setDetectedType] = useState<string | null>(null);
  const [initialScale, setInitialScale] = useState<boolean>(false);
  const fileUrlRef = useRef<string | null>(null);
  const viewerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const createdUrlRef = useRef<string | null>(null); // Track URLs we create
  
  useEffect(() => {
    // Cleanup blob URL on component unmount only
    return () => {
      // Only cleanup if we created the blob URL
      if (createdUrlRef.current) {
        URL.revokeObjectURL(createdUrlRef.current);
        createdUrlRef.current = null;
      }
    };
  }, []); // Empty dependency array - only cleanup on unmount

  useEffect(() => {
    if (!file) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    // If it's already a URL string, use it directly (don't create or cleanup)
    if (typeof file === 'string') {
      fileUrlRef.current = file;
      // We didn't create this URL, so don't track it for cleanup
    }
    // Only create blob URL for File objects
    else if (file instanceof File) {
      // Clean up previous blob URL if we created one
      if (createdUrlRef.current) {
        URL.revokeObjectURL(createdUrlRef.current);
        createdUrlRef.current = null;
      }
      // Create new blob URL
      const newUrl = URL.createObjectURL(file);
      fileUrlRef.current = newUrl;
      createdUrlRef.current = newUrl; // Track that we created this URL
      console.log('Created blob URL for file:', file.name, newUrl);
    }
    
    // Detect file type
    let type = fileType;
    if (fileType === 'auto') {
      if (file instanceof File) {
        const fileName = file.name.toLowerCase();
        if (fileName.endsWith('.pdf')) {
          type = 'pdf';
        } else if (fileName.endsWith('.docx') || fileName.endsWith('.doc')) {
          type = 'docx';
        }
      } else if (typeof file === 'string') {
        // For regular file paths
        if (file.toLowerCase().includes('.pdf')) {
          type = 'pdf';
        } else if (file.toLowerCase().includes('.docx') || file.toLowerCase().includes('.doc')) {
          type = 'docx';
        }
        // Note: For blob URLs, the fileType prop should be explicitly provided
      }
    }
    setDetectedType(type);
    
    // Set loading to false after type detection
    if (type) {
      setLoading(false);
    }
  }, [file, fileType]);

  const handleDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPageNumber(1);
    setLoading(false);
    // Reset initial scale flag when a new document loads
    setInitialScale(false);
  };

  const handlePageLoadSuccess = (page: any) => {
    // Only set initial scale once per document
    if (!initialScale && containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth - 40; // Account for padding
      const pageOriginalWidth = page.originalWidth;
      const pageOriginalHeight = page.originalHeight;
      
      // For landscape documents (width > height), fit to container width
      if (pageOriginalWidth > pageOriginalHeight) {
        const optimalScale = containerWidth / pageOriginalWidth;
        // Set scale to fit width, but don't exceed 100%
        setScale(Math.min(optimalScale, 1.0));
      }
      setInitialScale(true);
    }
  };

  const handleDocumentLoadError = (error: Error) => {
    const errorMessage = `Failed to load PDF document: ${error.message}`;
    setError(errorMessage);
    setLoading(false);
    onError?.(errorMessage);
  };


  const goToPrevPage = () => setPageNumber(prev => Math.max(prev - 1, 1));
  const goToNextPage = () => setPageNumber(prev => Math.min(prev + 1, numPages));
  const zoomIn = () => {
    setScale(prev => Math.min(prev + 0.2, 3.0));
    setInitialScale(true); // Prevent auto-scaling after manual zoom
  };
  const zoomOut = () => {
    setScale(prev => Math.max(prev - 0.2, 0.3));
    setInitialScale(true); // Prevent auto-scaling after manual zoom
  };
  const resetZoom = () => {
    setScale(1.0);
    setInitialScale(true); // Prevent auto-scaling after manual reset
  };
  const rotateDocument = () => setRotation(prev => (prev + 90) % 360);
  
  const toggleFullscreen = useCallback(() => {
    if (!isFullscreen) {
      // Enter fullscreen
      if (viewerRef.current?.requestFullscreen) {
        viewerRef.current.requestFullscreen();
      } else if ((viewerRef.current as any)?.webkitRequestFullscreen) {
        (viewerRef.current as any).webkitRequestFullscreen();
      } else if ((viewerRef.current as any)?.msRequestFullscreen) {
        (viewerRef.current as any).msRequestFullscreen();
      }
      setIsFullscreen(true);
    } else {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        (document as any).webkitExitFullscreen();
      } else if ((document as any).msExitFullscreen) {
        (document as any).msExitFullscreen();
      }
      setIsFullscreen(false);
    }
  }, [isFullscreen]);

  // Listen for fullscreen changes to sync state
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!(document.fullscreenElement || 
                                        (document as any).webkitFullscreenElement || 
                                        (document as any).msFullscreenElement);
      setIsFullscreen(isCurrentlyFullscreen);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);
    
    // Handle ESC key in fullscreen
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFullscreen) {
        toggleFullscreen();
      }
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFullscreen, toggleFullscreen]);

  const renderPDFViewer = () => {
    // Use the stored blob URL or the file string
    const fileUrl = fileUrlRef.current || (typeof file === 'string' ? file : null);
    
    if (!fileUrl) {
      return <div className="loading-spinner">Preparing document...</div>;
    }
    
    return (
      <div className="pdf-viewer-container" ref={containerRef}>
        <Document
          file={fileUrl}
          onLoadSuccess={handleDocumentLoadSuccess}
          onLoadError={handleDocumentLoadError}
          loading={<div className="loading-spinner">Loading PDF...</div>}
        >
          <Page
            pageNumber={pageNumber}
            scale={scale}
            rotate={rotation}
            loading={<div className="loading-spinner">Loading page...</div>}
            renderTextLayer={true}
            renderAnnotationLayer={true}
            onLoadSuccess={handlePageLoadSuccess}
          />
        </Document>
      </div>
    );
  };

  const renderDocxViewer = () => {
    // Use the stored blob URL or the file string
    const fileUrl = fileUrlRef.current || (typeof file === 'string' ? file : null);
    
    if (!fileUrl) {
      return <div className="loading-spinner">Preparing document...</div>;
    }
    
    const docs = [{
      uri: fileUrl,
      fileName: file instanceof File ? file.name : 'document.docx'
    }];

    return (
      <div className="docx-viewer-container" style={{ transform: `scale(${scale})`, transformOrigin: 'top left' }}>
        <DocViewer
          documents={docs}
          pluginRenderers={DocViewerRenderers}
          config={{
            header: {
              disableHeader: true,
            },
          }}
          style={{ height: height - (showToolbar ? 60 : 0) }}
        />
      </div>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="document-viewer-loading">
          <div className="loading-spinner">Loading document...</div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="document-viewer-error">
          <ErrorOutlineOutlined className="error-icon" />
          <p>{error}</p>
          <button onClick={() => window.location.reload()} className="retry-button">
            <RefreshOutlined /> Try Again
          </button>
        </div>
      );
    }

    if (!file) {
      return (
        <div className="document-viewer-empty">
          <p>No document selected</p>
        </div>
      );
    }

    switch (detectedType) {
      case 'pdf':
        return renderPDFViewer();
      case 'docx':
        return renderDocxViewer();
      default:
        return (
          <div className="document-viewer-error">
            <ErrorOutlineOutlined className="error-icon" />
            <p>Unsupported file type. Please select a PDF or Word document.</p>
          </div>
        );
    }
  };

  return (
    <div className={`document-viewer ${className} ${isFullscreen ? 'fullscreen' : ''}`} ref={viewerRef}>
      {showToolbar && (
        <div className="document-viewer-toolbar">
          <div className="toolbar-section">
            {detectedType === 'pdf' && numPages > 0 && (
              <>
                <button 
                  onClick={goToPrevPage} 
                  disabled={pageNumber <= 1}
                  title="Previous page"
                >
                  <NavigateBeforeOutlined />
                </button>
                <span className="page-info">
                  Page {pageNumber} of {numPages}
                </span>
                <button 
                  onClick={goToNextPage} 
                  disabled={pageNumber >= numPages}
                  title="Next page"
                >
                  <NavigateNextOutlined />
                </button>
              </>
            )}
          </div>
          
          <div className="toolbar-section">
            <button onClick={zoomOut} disabled={scale <= 0.3} title="Zoom out">
              <ZoomOutOutlined />
            </button>
            <span className="zoom-level">{Math.round(scale * 100)}%</span>
            <button onClick={zoomIn} disabled={scale >= 3.0} title="Zoom in">
              <ZoomInOutlined />
            </button>
            <button onClick={resetZoom} title="Reset zoom">
              <RefreshOutlined />
            </button>
          </div>

          {detectedType === 'pdf' && (
            <div className="toolbar-section">
              <button onClick={rotateDocument} title="Rotate document">
                <RotateLeftOutlined />
              </button>
            </div>
          )}

          <div className="toolbar-section">
            <button onClick={toggleFullscreen} title={isFullscreen ? "Exit fullscreen (ESC)" : "Enter fullscreen"}>
              {isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
            </button>
          </div>
        </div>
      )}
      
      <div 
        className="document-viewer-content" 
        style={{ height: height - (showToolbar ? 60 : 0) }}
      >
        {renderContent()}
      </div>
    </div>
  );
};

export default DocumentViewer;